import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Users, UserPlus, Trash2, Clock, ExternalLink, Settings2, Folder, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { listTeam, listInvites, createInvite, revokeInvite, updateTeamMember, getCurrentUser } from '@/lib/auth';
import { useT } from '@/lib/LanguageContext';
import { projectsAPI } from '@/lib/api';
import { format, parseISO } from 'date-fns';

const roleBadge = (role) => {
  const styles = {
    owner: 'bg-purple-100 text-purple-700',
    admin: 'bg-blue-100 text-blue-700',
    member: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[role] || styles.member}`}>
      {role}
    </span>
  );
};

// Detect if an email is external (different domain from the current user)
const isExternalEmail = (email, currentUserEmail) => {
  if (!email || !currentUserEmail) return false;
  const domain = (e) => e.split('@')[1]?.toLowerCase();
  return domain(email) !== domain(currentUserEmail);
};

export default function TeamMembers({ embedded = false }) {
  const t = useT();
  const currentUser = getCurrentUser();
  const isOwner = currentUser?.role === 'owner';
  const isAdmin = isOwner || currentUser?.role === 'admin';

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteProjectIds, setInviteProjectIds] = useState([]);
  const [inviteCanSignal, setInviteCanSignal] = useState(true);
  const [inviting, setInviting] = useState(false);

  // Permissions sheet state
  const [permSheet, setPermSheet] = useState(null); // member object
  const [permProjectIds, setPermProjectIds] = useState([]);
  const [permCanSignal, setPermCanSignal] = useState(true);
  const [savingPerms, setSavingPerms] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [teamData, inviteData, projectRes] = await Promise.all([
        listTeam(),
        listInvites(),
        projectsAPI.list(),
      ]);
      setMembers(Array.isArray(teamData) ? teamData : []);
      setInvites(Array.isArray(inviteData) ? inviteData : []);
      setProjects(Array.isArray(projectRes.data) ? projectRes.data : []);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Invite ────────────────────────────────────────────────────────────────────

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const result = await createInvite({
        email: inviteEmail.trim(),
        role: inviteRole,
        project_ids: inviteProjectIds,
        can_access_signal: inviteCanSignal,
      });
      const link = `${window.location.origin}/invite/${result.token}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      if (result.email_sent) {
        toast.success(`Invite email sent to ${inviteEmail.trim()}`);
      } else {
        toast.warning('Invite created but email could not be sent — link copied to clipboard. Share it manually.', { duration: 8000 });
      }
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteProjectIds([]);
      setInviteCanSignal(true);
      await load();
    } catch (err) {
      toast.error('Failed to create invite', { description: err.message });
    } finally {
      setInviting(false);
    }
  };

  const toggleInviteProject = (id) => {
    setInviteProjectIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ── Revoke invite ─────────────────────────────────────────────────────────────

  const handleRevoke = async (inviteId) => {
    try {
      await revokeInvite(inviteId);
      toast.success('Invite revoked');
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch {
      toast.error('Failed to revoke invite');
    }
  };

  // ── Role + active ─────────────────────────────────────────────────────────────

  const handleToggleActive = async (member) => {
    if (member.role === 'owner') return;
    try {
      await updateTeamMember(member.id, { is_active: !member.is_active });
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, is_active: !m.is_active } : m));
      toast.success(member.is_active ? 'User deactivated' : 'User reactivated');
    } catch {
      toast.error('Failed to update member');
    }
  };

  const handleRoleChange = async (member, newRole) => {
    if (member.role === 'owner') return;
    try {
      await updateTeamMember(member.id, { role: newRole });
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, role: newRole } : m));
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    }
  };

  // ── Permissions sheet ─────────────────────────────────────────────────────────

  const openPermSheet = (member) => {
    setPermSheet(member);
    setPermProjectIds(member.project_ids || []);
    setPermCanSignal(member.can_access_signal !== false);
  };

  const togglePermProject = (id) => {
    setPermProjectIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const savePerms = async () => {
    if (!permSheet) return;
    setSavingPerms(true);
    try {
      await updateTeamMember(permSheet.id, {
        project_ids: permProjectIds,
        can_access_signal: permCanSignal,
      });
      setMembers((prev) =>
        prev.map((m) =>
          m.id === permSheet.id
            ? { ...m, project_ids: permProjectIds, can_access_signal: permCanSignal }
            : m
        )
      );
      toast.success('Permissions saved');
      setPermSheet(null);
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSavingPerms(false);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return format(typeof d === 'string' ? parseISO(d) : new Date(d), 'MMM d, yyyy'); } catch { return '—'; }
  };

  const projectName = (id) => projects.find((p) => p._id === id)?.name || id;

  const Wrap = embedded ? React.Fragment : Layout;
  const wrapProps = embedded ? {} : { title: t('settings.team_title') };

  return (
    <Wrap {...wrapProps}>
      <div className={embedded ? 'max-w-4xl' : 'container mx-auto p-8 max-w-4xl'}>
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('settings.team_title')}
            </h1>
            <p className="text-muted-foreground">
              Manage who has access to Stagehead OS, assign projects, and control feature permissions.
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => setInviteDialogOpen(true)}
              className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('settings.invite_member')}
            </Button>
          )}
        </div>

        {/* Active Members */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#A62B1F]" />
              <div>
                <CardTitle>Active Members</CardTitle>
                <CardDescription>{members.filter(m => m.is_active).length} active</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{t('settings.loading_team')}</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{t('settings.no_team')}</p>
            ) : (
              <div className="divide-y divide-[#E3DDD1]">
                {members.map((member) => {
                  const isExternal = isExternalEmail(member.email, currentUser?.email);
                  const assignedProjects = (member.project_ids || []);
                  return (
                    <div key={member.id} className="py-3 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-[#1E2A33] truncate">
                            {member.full_name || '(no name)'}
                          </p>
                          {roleBadge(member.role)}
                          {isExternal && (
                            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium">
                              <ExternalLink className="w-2.5 h-2.5" />
                              External
                            </span>
                          )}
                          {!member.is_active && (
                            <span className="text-xs text-red-500 font-medium">Deactivated</span>
                          )}
                          {member.id === currentUser?.id && (
                            <span className="text-xs text-[#5C6670]">(you)</span>
                          )}
                        </div>
                        <p className="text-xs text-[#5C6670] truncate mt-0.5">{member.email}</p>

                        {/* Project assignment tags */}
                        {member.role === 'member' && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {assignedProjects.length === 0 ? (
                              <span className="text-[11px] text-[#9AA3AB] italic">All projects (no restrictions)</span>
                            ) : (
                              assignedProjects.map((pid) => (
                                <span key={pid} className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                  <Folder className="w-2.5 h-2.5" />
                                  {projectName(pid)}
                                </span>
                              ))
                            )}
                            {member.can_access_signal === false && (
                              <span className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                                <Radio className="w-2.5 h-2.5" />
                                No Signal access
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {isOwner && member.id !== currentUser?.id && member.role !== 'owner' && (
                        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                          <Select
                            value={member.role}
                            onValueChange={(v) => handleRoleChange(member, v)}
                          >
                            <SelectTrigger className="h-8 w-[100px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2.5 text-xs"
                            onClick={() => openPermSheet(member)}
                          >
                            <Settings2 className="w-3.5 h-3.5 mr-1" />
                            Permissions
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`h-8 px-2.5 text-xs ${member.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                            onClick={() => handleToggleActive(member)}
                          >
                            {member.is_active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Invites */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#A62B1F]" />
                <div>
                  <CardTitle>Pending Invites</CardTitle>
                  <CardDescription>{invites.length} awaiting acceptance</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {invites.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No pending invites.</p>
              ) : (
                <div className="divide-y divide-[#E3DDD1]">
                  {invites.map((invite) => (
                    <div key={invite.id} className="py-3 flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#1E2A33] truncate">{invite.email}</p>
                          {roleBadge(invite.role)}
                        </div>
                        <p className="text-xs text-[#5C6670]">Expires: {fmtDate(invite.expires_at)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#5C6670] hover:text-[#1E2A33] shrink-0 text-xs gap-1"
                        onClick={() => {
                          const link = `${window.location.origin}/invite/${invite.token}`;
                          navigator.clipboard.writeText(link);
                          toast.success('Invite link copied');
                        }}
                        title="Copy invite link"
                      >
                        <ExternalLink className="w-3 h-3" /> Copy link
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 shrink-0"
                        onClick={() => handleRevoke(invite.id)}
                        title="Revoke invite"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Invite Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('settings.invite_member')}</DialogTitle>
            <DialogDescription>
              They'll receive a unique link to set up their account. Links expire after 7 days.
              External (non-company) email addresses are supported.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="invite-email">{t('settings.invite_email')}</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="rep@partnercompany.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member — standard access</SelectItem>
                  <SelectItem value="admin">Admin — can invite others &amp; manage settings</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Project assignment (only relevant for members) */}
            {inviteRole === 'member' && projects.length > 0 && (
              <div>
                <Label className="mb-1.5 block">
                  Project access
                  <span className="text-xs text-muted-foreground ml-1.5 font-normal">
                    (leave all unchecked for access to all projects)
                  </span>
                </Label>
                <div className="space-y-1.5 border rounded-md p-3 max-h-40 overflow-y-auto">
                  {projects.map((p) => (
                    <label key={p._id} className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox
                        checked={inviteProjectIds.includes(p._id)}
                        onCheckedChange={() => toggleInviteProject(p._id)}
                      />
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: p.color || '#9AA3AB' }}
                      />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Signal access */}
            {inviteRole === 'member' && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="text-sm font-medium">Signal access</p>
                  <p className="text-xs text-muted-foreground">Campaigns and Social Calendar</p>
                </div>
                <Switch checked={inviteCanSignal} onCheckedChange={setInviteCanSignal} />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={inviting}
                className="bg-[#A62B1F] hover:bg-[#8C2318] text-white"
              >
                {inviting ? t('common.loading') : 'Create Invite & Copy Link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Permissions Sheet ─────────────────────────────────────────────────── */}
      <Sheet open={!!permSheet} onOpenChange={(open) => !open && setPermSheet(null)}>
        <SheetContent className="w-[380px]">
          <SheetHeader>
            <SheetTitle>Permissions — {permSheet?.full_name || permSheet?.email}</SheetTitle>
            <SheetDescription>
              Control which projects this member can access and which features are visible to them.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            {/* Project assignment */}
            <div>
              <p className="text-sm font-medium mb-1">Project access</p>
              <p className="text-xs text-muted-foreground mb-2">
                Leave all unchecked to give access to all projects.
              </p>
              <div className="space-y-1.5 border rounded-md p-3 max-h-52 overflow-y-auto">
                {projects.map((p) => (
                  <label key={p._id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={permProjectIds.includes(p._id)}
                      onCheckedChange={() => togglePermProject(p._id)}
                    />
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: p.color || '#9AA3AB' }}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>

            {/* Signal access toggle */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Signal access</p>
                <p className="text-xs text-muted-foreground">Campaigns and Social Calendar</p>
              </div>
              <Switch checked={permCanSignal} onCheckedChange={setPermCanSignal} />
            </div>

            <Button
              className="w-full bg-[#A62B1F] hover:bg-[#8C2318] text-white"
              onClick={savePerms}
              disabled={savingPerms}
            >
              {savingPerms ? t('common.loading') : 'Save Permissions'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </Wrap>
  );
}
